import React from 'react';
import get from 'lodash/get';
import { DEVIATION_PATHS } from '@wix/da-url/dist/src/constants';
import { Url } from '@wix/da-url';
import { DeviationTypes, DeviationResourceType, DeviationSuggestionReasons } from '../types/deviation';
import { getFeature } from '../DaEditorWrapper/Features/helper';
import { MEDIA_COUNT_FEATURE, FIRST_IMAGE_FEATURE } from '../DaEditorWrapper/Features';
import DeviationMediaService from '../utils/DeviationMediaService';
import { BackgroundImageDisplayMode, BackgroundType } from '../Background/types';
const CHARS_BEFORE_LONG_STATUS = 110;
export const JOURNAL_CATEGORY_PREFIXES = [
    'journals',
    'personaljournal'
];
// for fullviews/art stage
const TINY_IMAGE_THRESHOLD_WIDTH = 400;
const TINY_IMAGE_THRESHOLD_HEIGHT = 400;
const TINY_IMAGE_THRESHOLD_WIDTH_MOBILE = 200;
const TINY_IMAGE_THRESHOLD_HEIGHT_MOBILE = 180;
export function styleFromWidthHeight({ width, height }, responsive = true) {
    // if nothing is set, always fill container
    if (!width && !height) {
        return {
            width: '100%',
            height: '100%'
        };
    }
    // if responsive mode, or both props are set - just use whatever is set
    if (responsive || width && height) {
        return {
            width,
            height
        };
    }
    // otherwise try to square it off
    return {
        width: width || height || '100%',
        height: height || width || '100%'
    };
}
export function getDeviationType(deviation) {
    return DeviationMediaService.getDeviationType(deviation);
}
/**
 * Returns a deviation's title. Some deviation types will return `undefined` since they have default placeholder titles.
 *
 * e.g. deviations with type `DeviationTypes.STATUS` will return `undefined` instead of `Untitled`
 */ export function getDeviationTitle(deviation) {
    var _deviation;
    return getDeviationType(deviation) === DeviationTypes.STATUS ? undefined : (_deviation = deviation) === null || _deviation === void 0 ? void 0 : _deviation.title;
}
export function getDeviationResourceType(deviation) {
    const type = getDeviationType(deviation);
    switch(type){
        case DeviationTypes.JOURNAL:
            return DeviationResourceType.JOURNAL;
        case DeviationTypes.POLL:
            return DeviationResourceType.POLL;
        case DeviationTypes.COMMISSION:
            return DeviationResourceType.COMMISSION;
        case DeviationTypes.STATUS:
            return isLegacyStatus(deviation) ? DeviationResourceType.STATUS : DeviationResourceType.STATUS_DEVIATION;
        case DeviationTypes.TIER:
            return DeviationResourceType.TIER;
        default:
            return DeviationResourceType.ART;
    }
}
export function isVisualDeviation(deviation) {
    return [
        DeviationTypes.FILM,
        DeviationTypes.IMAGE,
        DeviationTypes.PDF
    ].includes(getDeviationType(deviation));
}
export function isTextualDeviation(deviation) {
    const type = DeviationMediaService.getDeviationType(deviation);
    if ([
        DeviationTypes.JOURNAL,
        DeviationTypes.LITERATURE,
        DeviationTypes.STATUS
    ].includes(type)) {
        if (type === DeviationTypes.STATUS && isLegacyStatus(deviation)) {
            return false;
        }
        return true;
    }
    return false;
}
export function isLegacyStatus(deviation) {
    var _deviation;
    if (((_deviation = deviation) === null || _deviation === void 0 ? void 0 : _deviation.typeId) === DeviationResourceType.STATUS) {
        return true;
    }
    return false;
}
export function isSimpleDeviation(deviation) {
    const isSimpleType = [
        DeviationTypes.COMMISSION,
        DeviationTypes.POLL
    ].includes(getDeviationType(deviation));
    return isSimpleType || isLegacyStatus(deviation);
}
export function isDeviationAuthor(deviation, user) {
    var _user;
    const authorUserId = get(deviation, 'extended.originalAuthor.userId', get(deviation, 'author.userId', false));
    return authorUserId === ((_user = user) === null || _user === void 0 ? void 0 : _user.userId);
}
export function isDeviationPixelArt(deviation) {
    return false; // we don't support pixel detection for now
}
export function isGifDeviation(deviation) {
    return DeviationMediaService.isGif(deviation);
}
export function isVideoDeviation(deviation) {
    return DeviationMediaService.getDeviationType(deviation) === DeviationTypes.FILM;
}
export function isDeviationTransparent(deviation) {
    // get our representative fullview file
    const fullview = DeviationMediaService.getLargestFullview(deviation);
    // and see if it's transparent
    return !!fullview.transparency;
}
export function isDeviationTextEditable(deviation) {
    const isTextEditable = !!deviation.isTextEditable;
    const hasLegacyTextEditUrl = !!deviation.legacyTextEditUrl;
    switch(getDeviationType(deviation)){
        case DeviationTypes.LITERATURE:
        case DeviationTypes.JOURNAL:
            return isTextEditable || hasLegacyTextEditUrl;
        case DeviationTypes.COMMISSION:
        case DeviationTypes.POLL:
        case DeviationTypes.STATUS:
            return isTextEditable;
        default:
            return false;
    }
}
export const isImageDeviation = (deviation)=>getDeviationType(deviation) === DeviationTypes.IMAGE;
export function getDeviationBasePath(deviation) {
    switch(getDeviationType(deviation)){
        case DeviationTypes.JOURNAL:
            return DEVIATION_PATHS.JOURNAL;
        case DeviationTypes.COMMISSION:
            return DEVIATION_PATHS.COMMISSION;
        case DeviationTypes.POLL:
            return DEVIATION_PATHS.POLL;
        case DeviationTypes.STATUS:
            return DEVIATION_PATHS.STATUS;
        case DeviationTypes.TIER:
            return DEVIATION_PATHS.TIER;
        default:
            return DEVIATION_PATHS.DEVIATION;
    }
}
export function getDeviationUrl(deviation) {
    const { deviationId, title: deviationTitle, author, url } = deviation;
    // on dev, we always want to use generated urls because otherwise
    // devpage inner links will lead to green
    // TODO why not just generate all urls???
    if (!url || !/^https:\/\/www.deviantart.com\//.test(url) && !/^https:\/\/sta.sh\//.test(url) || process.env.NODE_ENV !== 'test' && process.env.NODE_ENV !== 'production') {
        return Url.deviationLink(deviationId, author && author.username, deviationTitle, getDeviationBasePath(deviation));
    }
    return url;
}
export function getDeviationPath(deviation) {
    const { deviationId, title: deviationTitle, author } = deviation;
    return Url.deviationPath(deviationId, author && author.username.toLowerCase(), deviationTitle, getDeviationBasePath(deviation));
}
export function isValidFileSrc(src) {
    return /^https:\/\/api-da\.wixmp\.com\//.test(src) || /^https:\/\/(.+)\.deviantart\.net\//.test(src) || /^https:\/\/(|images-)wixmp-ed30a86b8c4ca887773594c2\.wixmp\.com\//.test(src) || /^https:\/\/(|images-)wixmp-395331243bc2089e8c09c8d3\.wixmp\.com\//.test(src);
}
export function getFilePixelSize(file) {
    return (file.width || 0) * (file.height || 0);
}
export function isFileLargerThanSize(file, width, height) {
    if (width && height && file.width && file.height) {
        return file.width > width || file.height > height;
    } else if (width && file.width) {
        return file.width > width;
    } else if (height && file.height) {
        return file.height > height;
    }
    return false;
}
export function isFileSizeBetween(file, minSize, maxSize) {
    if (minSize !== undefined && file.filesize && file.filesize < minSize) {
        return false;
    }
    if (maxSize !== undefined && file.filesize && file.filesize > maxSize) {
        return false;
    }
    return true;
}
export function isFileLargerThanSizeOrEqual(file, width, height) {
    if (width && height && file.width && file.height) {
        return file.width >= width || file.height >= height;
    } else if (width && file.width) {
        return file.width >= width;
    } else if (height && file.height) {
        return file.height >= height;
    }
    return false;
}
export function isFileTiny(file, isMobile) {
    if (!file.width || !file.height) {
        return false;
    }
    const thresholdWidth = isMobile ? TINY_IMAGE_THRESHOLD_WIDTH_MOBILE : TINY_IMAGE_THRESHOLD_WIDTH;
    const thresholdHeight = isMobile ? TINY_IMAGE_THRESHOLD_HEIGHT_MOBILE : TINY_IMAGE_THRESHOLD_HEIGHT;
    return file.width < thresholdWidth && file.height < thresholdHeight;
}
export function scaleToSize(content, container) {
    // if anything is missing, bail
    if (!content.width || !content.height || !container.width || !container.height) {
        return;
    }
    // if our content is small enough, noop
    if (content.width <= container.width && content.height <= container.height) {
        return {
            width: content.width,
            height: content.height
        };
    }
    // we need to check along which axis do we need to scale more (ie. smaller scale factor)
    const widthScale = container.width / content.width;
    const heightScale = container.height / content.height;
    if (widthScale < heightScale) {
        return {
            width: container.width,
            height: content.height * widthScale
        };
    }
    return {
        width: content.width * heightScale,
        height: container.height
    };
}
export function getIsShortStatus({ textContent }, ignoreMedia = false) {
    if (!textContent) {
        return false;
    }
    const isLegacy = textContent.html.type === 'writer';
    let hasMedia = false;
    if (!isLegacy && !ignoreMedia) {
        var _mediaCounts;
        const mediaCounts = getFeature(MEDIA_COUNT_FEATURE, textContent.html.features);
        const { images = 0, video = 0, previews = 0 } = ((_mediaCounts = mediaCounts) === null || _mediaCounts === void 0 ? void 0 : _mediaCounts.data) || {};
        if (images > 0 || video > 0 || previews > 0) {
            hasMedia = true;
        }
    }
    return textContent.excerpt.length < CHARS_BEFORE_LONG_STATUS && !hasMedia;
}
export function getStatusImage(deviation) {
    let backgroundItem;
    if (deviation.textContent.html.type === 'writer') {
        const statusItems = deviation.status && deviation.status.items || [];
        const bg = statusItems.find((item)=>item.type === 'thumb_background_deviation');
        backgroundItem = bg && bg.deviation || undefined;
    } else {
        const first = getFeature(FIRST_IMAGE_FEATURE, deviation.textContent.html.features);
        if (first) {
            backgroundItem = first.data.deviation;
        }
    }
    return backgroundItem;
}
export function getStatusBackgroundProps(deviation) {
    const isShortStatus = getIsShortStatus(deviation);
    const image = getStatusImage(deviation);
    const defaultProps = isShortStatus && image ? {
        image,
        backgroundType: BackgroundType.IMAGE,
        imageDisplayMode: BackgroundImageDisplayMode.COVER
    } : undefined;
    return get(deviation, 'status.background', defaultProps);
}
export function getTranslatedDeviationTitle(deviation, t) {
    const deviationTitle = getDeviationTitle(deviation);
    const { author } = deviation;
    if (!author) {
        return deviationTitle;
    }
    if (getDeviationType(deviation) === DeviationTypes.STATUS) {
        return t('deviation.deviationTitle.status', {
            author: author.username
        });
    }
    return t('deviation.deviationTitle', {
        title: deviationTitle,
        author: author.username
    });
}
export function deviationTypeToTranslationKey(type) {
    const typeMap = {
        [DeviationTypes.COMMISSION]: 'commission',
        [DeviationTypes.POLL]: 'poll',
        [DeviationTypes.STATUS]: 'status',
        [DeviationTypes.JOURNAL]: 'journal',
        [DeviationTypes.LITERATURE]: 'literature'
    };
    return typeMap.hasOwnProperty(type) ? `common.${typeMap[type]}` : `common.deviation`;
}
export function getSource(media) {
    if (!media || media.length === 0) {
        return null;
    }
    // TODO: pick the size that fits the screen size the best
    // get the largest quality available for now
    const [maxQuality] = media.map((file)=>parseInt(`${file.quality}`, 10)).sort((a, b)=>b - a);
    return media.find((file)=>file.quality ? file.quality.startsWith(`${maxQuality}`) : false);
}
export function getBlurredThumbStyles(deviation) {
    // TODO: use actual blurred thumb image, this is fake
    const thumb = DeviationMediaService.getSmallestThumb(deviation);
    if (!thumb) {
        return;
    }
    return {
        backgroundImage: `url('${thumb.src}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        overflow: 'hidden',
        filter: 'blur(25px)'
    };
}
export function getSuggestionReasonsByPriority(deviation) {
    const priority = [
        DeviationSuggestionReasons.Boosted,
        DeviationSuggestionReasons.DailyDeviation,
        DeviationSuggestionReasons.WatchingUser,
        DeviationSuggestionReasons.WatchingGroup,
        DeviationSuggestionReasons.WatchingTopic,
        DeviationSuggestionReasons.Recommended,
        DeviationSuggestionReasons.NewDeviant,
        DeviationSuggestionReasons.Popular
    ];
    // just filter
    return priority.filter((reason)=>{
        var _deviation_suggestedReasons_includes, _this;
        return (_this = deviation.suggestedReasons) === null || _this === void 0 ? void 0 : (_deviation_suggestedReasons_includes = _this.includes) === null || _deviation_suggestedReasons_includes === void 0 ? void 0 : _deviation_suggestedReasons_includes.call(_this, reason);
    });
}
export const TextDumboType = {
    ANTFART: 'antfart',
    NANO: 'nano',
    MICRO: 'micro',
    TINY: 'tiny',
    SMALL: 'small',
    MEDIUM: 'medium',
    BIG: 'big'
};
export const TextDumboTypeSize = {
    ANTFART: 66,
    NANO: 150,
    MICRO: 170,
    TINY: 250,
    SMALL: 275,
    MEDIUM: 400,
    BIG: 600
};
export function getTextDumboTypeBySize(size) {
    if (size <= TextDumboTypeSize.ANTFART) {
        return TextDumboType.ANTFART;
    }
    if (size <= TextDumboTypeSize.NANO) {
        return TextDumboType.NANO;
    }
    if (size <= TextDumboTypeSize.MICRO) {
        return TextDumboType.MICRO;
    }
    if (size <= TextDumboTypeSize.TINY) {
        return TextDumboType.TINY;
    }
    if (size >= TextDumboTypeSize.BIG) {
        return TextDumboType.BIG;
    }
    if (size >= TextDumboTypeSize.MEDIUM) {
        return TextDumboType.MEDIUM;
    }
    return TextDumboType.SMALL;
}
/** Returns the tier this deviation is available under (if any) */ export function getPrimaryTier(deviation) {
    var _deviation;
    return (_deviation = deviation) === null || _deviation === void 0 ? void 0 : _deviation.primaryTier;
}
/** Is the current user subscribed to the tier this deviation is part of? */ export function getIsUserSubscribed(deviation) {
    var _getPrimaryTier_tier, _getPrimaryTier;
    return !!((_getPrimaryTier_tier = (_getPrimaryTier = getPrimaryTier(deviation)) === null || _getPrimaryTier === void 0 ? void 0 : _getPrimaryTier.tier) === null || _getPrimaryTier_tier === void 0 ? void 0 : _getPrimaryTier_tier.isUserSubscribed);
}
/** Tier desciprion != deviation description */ export function getTierDescription(deviation) {
    var _deviation_textContent, _deviation;
    return (_deviation_textContent = (_deviation = deviation) === null || _deviation === void 0 ? void 0 : _deviation.textContent) === null || _deviation_textContent === void 0 ? void 0 : _deviation_textContent.excerpt;
}
export const getTranslatedTierInfo = (deviation, t)=>{
    var _deviation_tier, _deviation, _deviation_tier_settings, _deviation_tier1, _deviation1;
    if (((_deviation_tier = (_deviation = deviation) === null || _deviation === void 0 ? void 0 : _deviation.tier) === null || _deviation_tier === void 0 ? void 0 : _deviation_tier.state) === 'pending_deletion') {
        return deviation.tier.isUserSubscribed ? t('deviationview.tierCard.info.pending_deletion.subscribed') : t('deviationview.tierCard.info.pending_deletion.unsubscribed');
    }
    switch((_deviation_tier_settings = (_deviation_tier1 = (_deviation1 = deviation) === null || _deviation1 === void 0 ? void 0 : _deviation1.tier) === null || _deviation_tier1 === void 0 ? void 0 : _deviation_tier1.settings) === null || _deviation_tier_settings === void 0 ? void 0 : _deviation_tier_settings.accessSettings){
        case 'all':
            return t('deviationview.tierCard.access.all');
        case 'future_only':
            return t('deviationview.tierCard.access.future_only');
        case 'limited_past_and_future':
            return t('deviationview.tierCard.access.limited_past_and_future');
        default:
            return undefined;
    }
};
export function getShouldDisplayPaywall(deviationType) {
    return ![
        DeviationTypes.TIER,
        DeviationTypes.JOURNAL,
        DeviationTypes.STATUS,
        DeviationTypes.LITERATURE
    ].includes(deviationType);
}
export function isDeviation(item) {
    return item.hasOwnProperty('deviationId');
}
export function isPCP(item) {
    return item.isPurchasable;
}
export function isAdoptable(item) {
    return item.isAdoptable;
}
/** Provides a unified way of accessing related content between old and new format. Do NOT use for rendering purposes */ export function getRelatedContentBlock(deviation, type) {
    var _deviation_extended_relatedContent_find, _this, _deviation_extended;
    return (_this = (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.relatedContent) === null || _this === void 0 ? void 0 : (_deviation_extended_relatedContent_find = _this.find) === null || _deviation_extended_relatedContent_find === void 0 ? void 0 : _deviation_extended_relatedContent_find.call(_this, (block)=>block.contentType === type);
}
/** Returns true if we expect/allow the deviation to have pcp */ export function getCanHavePremiumDownload(deviation) {
    const deviationType = getDeviationType(deviation);
    return [
        // todo?: DeviationTypes.PDF, DeviationTypes.FILM,
        DeviationTypes.IMAGE
    ].includes(deviationType);
}
